import { REFERRAL_ADD_ROUTE, REFERRAL_STATUS, REFERRAL_STATUS_BADGE_COLORS } from '@constant/referral';
import { disableLoader, enableLoader, showSuccess } from '@context/actions';
import { getOrderByTenantIdAndGuestId } from '@storeData/order';
import { getGuestReferralsByGuestId, getReferralConfig } from '@storeData/referral';
import { getUserByTenantAndMobile } from '@storeData/user';
import { GuestReferral, ReferralConfig } from '@type/referral';
import { navigateTo } from '@util/routerService';
import { useCallback, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useDispatch, useSelector } from 'react-redux';

import {
    CopyIcon,
    GiftBoxIcon,
    ShareIcon
} from '@element/icons';
type FetchState = 'idle' | 'loading' | 'success' | 'error';

const REFERRAL_UNLOCK_MESSAGE = 'Book your first appointment & complete your first order to unlock your referral code. Invite friends and earn exciting discounts on every successful referral!';

const getStatusBadgeColor = (status: string) => {
    const normalizedStatus = status?.trim();
    const matchedStatus = Object.keys(REFERRAL_STATUS_BADGE_COLORS).find(
        (key) => key.toLowerCase() === normalizedStatus.toLowerCase()
    );

    return matchedStatus ? REFERRAL_STATUS_BADGE_COLORS[matchedStatus] : REFERRAL_STATUS_BADGE_COLORS[REFERRAL_STATUS.CONVERTED];
};

const formatStatusLabel = (status: string) => {
    if (!status) return '-';
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

function ReferralPage() {
    const dispatch = useDispatch();
    const [cookie] = useCookies();
    const [userData, setUserData] = useState(cookie['user']);
    const store = useSelector((state: any) => state);
    const storeMetaData = useSelector((state: any) => state.store ? state.store.storeMetaData : null);
    const configData = useSelector((state: any) => state.store?.storeData?.configData);
    const languageData: any = store.languageKeywords;
    const [referrals, setReferrals] = useState<GuestReferral[]>([]);
    const [referralConfig, setReferralConfig] = useState<ReferralConfig | null>(null);
    const [fetchState, setFetchState] = useState<FetchState>('idle');
    const [errorMessage, setErrorMessage] = useState('');
    const [copied, setCopied] = useState(false);
    const [hasOrderHistory, setHasOrderHistory] = useState<boolean | null>(null);
    const referralCode = userData?.referralCode || '';
    const referralBenefit = referralConfig?.referredByAmount ? `${configData?.currencySymbol}${referralConfig?.referredByAmount}` : `${referralConfig?.referredByPercentage}%`;
    const referredToBenefit = referralConfig?.referredToAmount ? `${configData?.currencySymbol}${referralConfig?.referredToAmount}` : `${referralConfig?.referredToPercentage}%`;
    const shareMessageFallback = languageData[`Refer a friend and get exciting benefit on each referral! When they will make their first bill they will get ${referredToBenefit} benefit and you'll get ${referralBenefit} benefit on your next bill.`]
        || `Refer a friend and get exciting benefit on each referral! When they will make their first bill they will get ${referredToBenefit} benefit and you'll get ${referralBenefit} benefit on your next bill.`;
    const referredFriendsHeading = languageData["Referred Friends"] || "Referred Friends";
    const unlockMessage = languageData[REFERRAL_UNLOCK_MESSAGE] || REFERRAL_UNLOCK_MESSAGE;

    useEffect(() => {
        if (cookie['user']) {
            const { tenantId, storeId, mobileNo } = cookie['user'];
            getUserByTenantAndMobile(tenantId, storeId, mobileNo).then((res: any) => {
                if (res?.data) {
                    setUserData(res.data);
                }
            });
        }
    }, [cookie]);

    useEffect(() => {
        if (!userData?.id || !userData?.tenantId) {
            setHasOrderHistory(null);
            return;
        }

        setHasOrderHistory(null);
        dispatch(enableLoader());

        getOrderByTenantIdAndGuestId(userData.id, userData.tenantId)
            .then((orders: any) => {
                const orderList = Array.isArray(orders)
                    ? orders
                    : Array.isArray(orders?.data)
                        ? orders.data
                        : [];
                setHasOrderHistory(orderList.length > 0);
                dispatch(disableLoader());
            })
            .catch(() => {
                setHasOrderHistory(false);
                dispatch(disableLoader());
            });
    }, [dispatch, userData?.id, userData?.tenantId]);

    useEffect(() => {
        if (hasOrderHistory !== true) return;

        const tenantId = storeMetaData?.tenantId ?? userData?.tenantId;
        const storeId = storeMetaData?.id ?? userData?.storeId;

        if (!tenantId || !storeId) return;

        getReferralConfig(tenantId, storeId)
            .then((config) => setReferralConfig(config))
            .catch(() => setReferralConfig(null));
    }, [hasOrderHistory, storeMetaData?.tenantId, storeMetaData?.id, userData?.tenantId, userData?.storeId]);

    const fetchReferrals = useCallback(() => {
        if (!userData?.id) {
            setFetchState('error');
            setErrorMessage(languageData["Please log in to view referrals."] || "Please log in to view referrals.");
            return;
        }

        setFetchState('loading');
        setErrorMessage('');
        dispatch(enableLoader());

        getGuestReferralsByGuestId(userData.id)
            .then((referralList: GuestReferral[]) => {
                setReferrals(referralList || []);
                setFetchState('success');
                dispatch(disableLoader());
            })
            .catch(() => {
                setReferrals([]);
                setFetchState('error');
                setErrorMessage(languageData["Unable to load referrals. Please try again."] || "Unable to load referrals. Please try again.");
                dispatch(disableLoader());
            });
    }, [dispatch, languageData, userData?.id]);

    useEffect(() => {
        if (hasOrderHistory !== true) return;
        fetchReferrals();
    }, [fetchReferrals, hasOrderHistory]);

    const copyReferralCode = (text: string) => {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.setAttribute('readonly', '');
        textArea.contentEditable = 'true';
        textArea.readOnly = false;
        document.body.appendChild(textArea);

        const range = document.createRange();
        range.selectNodeContents(textArea);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
        textArea.setSelectionRange(0, text.length);
        textArea.focus();

        let copied = false;
        try {
            copied = document.execCommand('copy');
        } catch {
            copied = false;
        }

        selection?.removeAllRanges();
        document.body.removeChild(textArea);
        return copied;
    };

    const handleCopyCode = () => {
        if (!referralCode) return;

        const isIOS = /iPad|iPhone|iPod/i.test(navigator.userAgent || '')
            || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

        const onSuccess = () => {
            setCopied(true);
            dispatch(showSuccess(languageData["Referral code copied"] || "Referral code copied", 2000));
            setTimeout(() => setCopied(false), 2000);
        };

        const onFail = () => {
            dispatch(showSuccess(languageData["Unable to copy referral code"] || "Unable to copy referral code", 2000));
        };

        // On iOS use execCommand in the same click gesture; clipboard API is unreliable
        if (isIOS) {
            if (copyReferralCode(referralCode)) onSuccess();
            else onFail();
            return;
        }

        if (navigator?.clipboard?.writeText) {
            navigator.clipboard.writeText(referralCode)
                .then(onSuccess)
                .catch(() => {
                    if (copyReferralCode(referralCode)) onSuccess();
                    else onFail();
                });
            return;
        }

        if (copyReferralCode(referralCode)) onSuccess();
        else onFail();
    };

    const handleShareCode = () => {
        if (!referralCode) return;

        const storeName = storeMetaData?.name || 'our salon';
        const baseUrl = (storeMetaData?.sUrl || '').trim();
        const shareMessage = `Hey! Use my referral code "${referralCode}" when booking services at ${storeName} and get ${referredToBenefit} off on your first visit! Book your appointment here: ${baseUrl}`;

        // Use HTTPS only — whatsapp:// fails with "scheme does not have a registered handler"
        // when WhatsApp is not installed or on desktop browsers.
        const whatsappUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareMessage)}`;
        window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    };

    if (hasOrderHistory === false) {
        return (
            <div className="referral-page-outer referral-locked-page">
                <div className="referral-hero">
                    <div className="referral-gift-wrap">
                        <GiftBoxIcon />
                    </div>
                    <h2 className="referral-hero-heading">
                        {languageData["Unlock Referral"] || "Unlock Referral"}
                    </h2>
                    <p className="referral-hero-message">{unlockMessage}</p>
                </div>
            </div>
        );
    }

    if (hasOrderHistory === null) {
        return <div className="referral-page-outer" />;
    }

    return (
        <div className="referral-page-outer">
            <div className="referral-hero">
                <div className="referral-gift-wrap">
                    <GiftBoxIcon />
                </div>
                <h2 className="referral-hero-heading">{languageData["Refer your friends"] || "Refer your friends"}</h2>
                <p className="referral-hero-message">{shareMessageFallback}</p>
            </div>

            {referralCode && (
                <div className="referral-code-card">
                    <span className="referral-code-label">
                        {languageData["Referral Code"] ? languageData["Referral Code"] : "Referral Code"}
                    </span>
                    <div className="referral-code-row">
                        <span className="referral-hero-heading">{referralCode}</span>
                        <div className="referral-code-actions">
                            <button
                                type="button"
                                className="referral-copy-btn"
                                onClick={handleCopyCode}
                                aria-label={languageData["Copy referral code"] || "Copy referral code"}
                            >
                                <CopyIcon />
                                {copied
                                    ? (languageData["Copied"] ? languageData["Copied"] : "Copied")
                                    : (languageData["Copy"] ? languageData["Copy"] : "Copy")}
                            </button>
                            <button
                                type="button"
                                className="referral-share-btn"
                                onClick={handleShareCode}
                                aria-label={languageData["Share referral code"] || "Share referral code"}
                            >
                                <ShareIcon />
                                {languageData["Share"] ? languageData["Share"] : "Share"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="referral-friends-section">
                <div className="referral-friends-header">
                    <h3 className="referral-friends-heading">{referredFriendsHeading}</h3>
                    <button
                        className="referral-btn"
                        onClick={() => navigateTo(REFERRAL_ADD_ROUTE)}
                    >
                        {languageData["Add Another Friend"] ? languageData["Add Another Friend"] : "+ Add Another Friend"}
                    </button>
                </div>

                {fetchState === 'error' && (
                    <div className="referral-error-state">
                        <p>{errorMessage}</p>
                        <button className="primary-btn rounded-btn border-btn" onClick={fetchReferrals}>
                            {languageData["Retry"] ? languageData["Retry"] : "Retry"}
                        </button>
                    </div>
                )}

                {fetchState === 'success' && !referrals.length && (
                    <div className="referral-empty-state">
                        {languageData["No referrals found."] ? languageData["No referrals found."] : "No referrals found."}
                    </div>
                )}

                {fetchState === 'success' && Boolean(referrals.length) && (
                    <div className="referral-list-wrap">
                        {referrals.map((referral, index) => (
                            <div className="referral-card" style={{ borderLeft: `5px solid ${getStatusBadgeColor(referral.status)}` }} key={referral.id || `${referral.referredToNumber}-${index}`}>
                                <div className="referral-card-top">
                                    <span className="referral-card-index">#{index + 1}</span>
                                    <span
                                        className="referral-status-badge"
                                        style={{ backgroundColor: getStatusBadgeColor(referral.status) }}
                                    >
                                        {formatStatusLabel(referral.status)}
                                    </span>
                                </div>
                                <div className="referral-card-name">{referral.referredToName || '-'}</div>
                                <div>{referral.referredToNumber || '-'}</div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default ReferralPage;
